import {useCallback, useMemo, useState} from 'react';
import {XHR_FAILED_STATE, XHR_LOADING_STATE} from '../../../assets/constants/constants';
import type {TXHR} from '../../../assets/constants/constansts.type';
import {logGoogleEvent} from '../../../utils/analytics';
import type {AssistantDraftPayload, ChatMessage, DraftResult} from './types';

interface UseAIMessageGeneratorArgs {
    errorMessage: string;
    submit: {
        label: string;
        onClick: (prompt: string, history: ChatMessage[]) => Promise<DraftResult>;
        xhrState?: TXHR;
    };
    approve: {
        label: string;
        onClick: (draft: AssistantDraftPayload) => Promise<void>;
        xhrState?: TXHR;
    };
    onClose?: () => void;
    analytics?: {
        contentId: string | number;
        sourceTypeId: string;
        context?: Record<string, unknown>;
    };
}

function createMessageId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useAIMessageGenerator({
    errorMessage,
    submit,
    approve,
    onClose = () => {},
    analytics,
}: UseAIMessageGeneratorArgs) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [promptText, setPromptText] = useState('');

    const draftLoading = submit.xhrState === XHR_LOADING_STATE;     
    const approveLoading = approve.xhrState === XHR_LOADING_STATE;

    const error = useMemo(() => {
        if(submit.xhrState === XHR_FAILED_STATE || approve.xhrState === XHR_FAILED_STATE) {
            return errorMessage;
        }
        return null;
    }, [submit.xhrState, approve.xhrState, errorMessage]);
    
    const reset = useCallback(() => {
        setMessages([]);
        setPromptText('');
    }, []);

    const submitPrompt = useCallback(async (promptOverride?: string) => {
        const trimmed = (promptOverride ?? promptText).trim();
        if(!trimmed || draftLoading) {
            return;
        }
        const priorMessages = messages;
        setMessages(prev => [...prev, {role: 'user', text: trimmed}]);
        setPromptText('');
        try {
            const result = await submit.onClick(trimmed, priorMessages);
            setMessages(prev => [...prev, {
                role: 'assistant',
                text: result?.text || '',
                parameterValues: result?.parameterValues || undefined,
                id: createMessageId(),
            }]);
        } catch {
            setMessages(prev => (prev[prev.length - 1]?.role === 'user' ? prev.slice(0, -1) : prev));
            setPromptText(trimmed);
        }
    }, [promptText, draftLoading, messages, submit.onClick]);

    const handleApprove = useCallback(async (draft: AssistantDraftPayload) => {
        if(approveLoading) {
            return;
        }
        if(analytics) {
            const messageIndex = messages.findIndex(message => message === draft);
            const assistantIndex = messageIndex >= 0
                ? messages.slice(0, messageIndex).filter(message => message.role === 'assistant').length
                : messages.filter(message => message.role === 'assistant').length - 1;
            logGoogleEvent('ai_message_generator_approve_click', analytics.contentId, analytics.sourceTypeId, {
                popup_name: 'aiMessageGenerator',
                ...(analytics.context ?? {}),
                button_name: 'approve',
                assistant_index: assistantIndex,
            });
        }
        await approve.onClick(draft);
        reset();
        onClose();
    }, [approveLoading, approve.onClick, onClose, analytics, messages]);

    return {
        messages,
        promptText,
        setPromptText,
        draftLoading,
        approveLoading,
        error,
        submitPrompt,
        handleApprove,
        reset,
    };
}
